import { Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import { capitalize } from "lodash";
import pluralize from "pluralize";
import { useBoolean } from "usehooks-ts";

import { DisabledArea } from "@/components/ui-v2/DisabledArea/DisabledArea";
import {
  FilePicker,
  FilePickerConfig,
  FilepickerFile,
} from "@/components/ui-v2/FilePicker";
import { FileSelectArea } from "@/components/ui-v2/FileSelectArea/FileSelectArea";
import {
  classes as fileSelectPreviewListClasses,
  FileSelectPreviewFile,
  FileSelectPreviewList,
} from "@/components/ui-v2/FileSelectPreviewList/FileSelectPreviewList";
import { useVideoEditorDialog } from "@/components/ui-v2/VideoEditorDialog/hooks/useVideoEditorDialog";
import { VideoEditorDialog } from "@/components/ui-v2/VideoEditorDialog/VideoEditorDialog";
import { UploadableModality } from "@/constants/dropzone";
import { SessionFileFragment } from "@/features/forge/forge-session/queries.graphql";
import { useShowSnackbar } from "@/hooks";

import { convertSessionFileToFilepickerFile } from "../../utils/utils";
import { getDisplayLabelForModality } from "./utils";

export const classes = {
  container: "FileUploader-container",
};

interface FileUploaderProps {
  modality: UploadableModality;
  filePickerConfig: FilePickerConfig;
  uploadedFiles: FileSelectPreviewFile[];
  onFileDeleted: (index: number) => void;
  onUploadFiles: (files: FilepickerFile[]) => void;
  onEditFile?: (index: number) => void;
  disabled?: boolean;
  disabledTooltip?: string;
  isDroppableEnabled?: boolean;
  droppableAccepts?: string[];
  droppableId?: string;
  renderFooter?: (
    file: FileSelectPreviewFile,
    index: number,
  ) => React.ReactNode;
  uploadFileTestId?: string;
  fileSelectAreaLabel?: string;
  addFileLabel?: string;
  bottomLabel?: string;
}

const Container = styled(Box, {
  shouldForwardProp: (prop) => prop !== "maxFiles",
})<{
  maxFiles: number;
}>(({ maxFiles }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  [`& .${fileSelectPreviewListClasses.fileListContainer}`]: {
    height: "100%",
    gridTemplateColumns: maxFiles === 1 ? "1fr" : "repeat(2, 1fr)",
    gridAutoRows: "1fr",
  },
}));

const StyledContainer = styled(Container, {
  shouldForwardProp: (prop) => prop !== "bottomLabel",
})<{
  bottomLabel?: string;
}>(({ theme, bottomLabel }) => ({
  ...(bottomLabel && {
    backgroundColor: theme.palette.secondary[5],
    border: `1px solid ${theme.palette.secondary[4]}`,
    borderRadius: theme.shape.borderRadius,
    flex: "1 1 0",
    padding: theme.spacing(2),
  }),
}));

export const FileUploader = (props: FileUploaderProps) => {
  const {
    modality,
    filePickerConfig,
    uploadedFiles,
    onFileDeleted,
    onUploadFiles,
    disabled = false,
    disabledTooltip = "",
    onEditFile,
    isDroppableEnabled = false,
    droppableAccepts = [],
    droppableId,
    renderFooter,
    uploadFileTestId,
    fileSelectAreaLabel,
    addFileLabel,
    bottomLabel,
  } = props;

  const {
    value: filePickerOpen,
    setTrue: openFilePicker,
    setFalse: closeFilePicker,
  } = useBoolean();

  const { showError } = useShowSnackbar();

  const {
    showVideoEditor,
    closeVideoEditor,
    pendingFile: pendingVideoEditorFile,
    handleFileSelectedForEditing,
  } = useVideoEditorDialog();

  const handleVideoEditorConfirm = (editedFile: FilepickerFile) => {
    // Convert the video editor result back to a FilepickerFile
    // and add the extracted frame to the uploaded files
    onUploadFiles([editedFile]);
    closeVideoEditor();
  };

  const processUploadedFiles = (files: FilepickerFile[]) => {
    // Check if any uploaded files are videos and modality is IMAGE
    const videoFiles = files.filter((file) =>
      file.contentType?.startsWith("video/"),
    );

    if (videoFiles.length > 0 && modality === "IMAGE") {
      // If there are video files and the modality is image, open the video editor for the first one
      const videoFile = videoFiles[0];

      if (!videoFile.width || !videoFile.height || !videoFile.src) {
        showError("Unable to load video file.");
        return;
      }

      const videoEditorFile = {
        width: videoFile.width,
        height: videoFile.height,
        videoSrc: videoFile.src,
      };

      handleFileSelectedForEditing(videoEditorFile);
    } else {
      // If no video files, proceed normally
      onUploadFiles(files);
    }
  };

  const handleDroppedFile = (droppedItem: SessionFileFragment) => {
    const filepickerFile = convertSessionFileToFilepickerFile(droppedItem);
    processUploadedFiles([filepickerFile]);
  };

  const maxFilePickerFiles = filePickerConfig.maxFiles - uploadedFiles.length;

  const maxVisibleFiles =
    filePickerConfig.maxVisibleFiles || (modality === "IMAGE" ? 4 : null);

  const humanReadableModality = getDisplayLabelForModality(modality);
  const defaultFileSelectAreaLabel = `Upload ${pluralize(capitalize(humanReadableModality), filePickerConfig.maxFiles)}`;

  return (
    <DisabledArea disabled={disabled} title={disabledTooltip}>
      <StyledContainer
        bottomLabel={bottomLabel}
        className={classes.container}
        maxFiles={filePickerConfig.maxFiles}
      >
        {uploadedFiles.length === 0 && (
          <FileSelectArea
            testId={uploadFileTestId}
            isDroppableEnabled={isDroppableEnabled}
            droppableId={`${droppableId}-file-select-area`}
            droppableAccepts={droppableAccepts}
            onDropped={handleDroppedFile}
            modality={modality}
            label={fileSelectAreaLabel ?? defaultFileSelectAreaLabel}
            disabled={disabled}
            onClick={openFilePicker}
            sx={{ mt: 2 }}
            onUpload={processUploadedFiles}
            accept={filePickerConfig.accept}
            bottomLabel={bottomLabel}
          />
        )}
        {uploadedFiles.length > 0 && (
          <FileSelectPreviewList
            isDroppableEnabled={isDroppableEnabled}
            modality={modality}
            droppableId={`${droppableId}-file-select-preview-list`}
            droppableAccepts={droppableAccepts}
            onDropped={handleDroppedFile}
            files={uploadedFiles}
            onOpenFilePicker={openFilePicker}
            onDeleteFile={onFileDeleted}
            onUpload={processUploadedFiles}
            onEditFileCrop={onEditFile}
            maxFiles={filePickerConfig.maxFiles}
            filesLayout={filePickerConfig.filesLayout}
            maxVisibleFiles={maxVisibleFiles || 0}
            renderFooter={renderFooter}
            addFileLabel={addFileLabel}
            uploadTestId={uploadFileTestId}
          />
        )}
        <FilePicker
          {...filePickerConfig}
          modality={modality}
          open={filePickerOpen}
          onClose={closeFilePicker}
          onFilesSelect={onUploadFiles}
          maxFiles={maxFilePickerFiles}
        />
        {!!pendingVideoEditorFile && (
          <VideoEditorDialog
            open={showVideoEditor && !!pendingVideoEditorFile}
            file={pendingVideoEditorFile}
            onClose={closeVideoEditor}
            onConfirm={handleVideoEditorConfirm}
          />
        )}
      </StyledContainer>
    </DisabledArea>
  );
};
